import React, { useState, useEffect } from "react";
import {
    Modal,
    Paper,
    Typography,
    Box,
    TextField,
    Button,
    IconButton,
    Collapse,
    Divider,
    Avatar,
    Tooltip,
    Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ChatIcon from "@mui/icons-material/Chat";
import PsychologyIcon from "@mui/icons-material/Psychology";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import BugReportIcon from "@mui/icons-material/BugReport";
import AgentCommunicationPanel from "./AgentCommunicationPanel";

// Dummy data generator
const generateDummyLogs = (agent) => [
    {
        id: 1,
        type: "INFO",
        title: `${agent} initiated venue discovery.`,
        details: [
            "Searching within 15-mile radius...",
            "Found 28 candidate gyms.",
            "Filtering based on price & availability."
        ],
        actor: "Agent",
        time: "10:12 AM",
    },
    {
        id: 2,
        type: "SUCCESS",
        title: "Booking confirmed at Roosevelt Park.",
        details: [
            "Rate: $45/hr.",
            "Confirmed with facility manager via SMS.",
            "Contract auto-uploaded to Documents tab."
        ],
        actor: "System",
        time: "10:45 AM",
    },
    {
        id: 3,
        type: "WARNING",
        title: "Low instructor availability detected in cluster.",
        details: [
            "Instructor pool: 3 active out of 12.",
            "Suggested: trigger hiring campaign in Pasadena cluster."
        ],
        actor: "Agent",
        time: "11:22 AM",
    },
    {
        id: 4,
        type: "TRAINING",
        title: "Feedback loop triggered from human correction.",
        details: [
            "Updated scoring weight: 0.15 → 0.20 for sponsor preference.",
            "Retraining job queued for 2:00 AM batch."
        ],
        actor: "Human",
        time: "12:05 PM",
    },
];

const typeIcons = {
    INFO: <ChatIcon fontSize="small" sx={{ color: "#2196f3" }} />,
    SUCCESS: <CheckCircleIcon fontSize="small" sx={{ color: "#4caf50" }} />,
    WARNING: <WarningAmberIcon fontSize="small" sx={{ color: "#ff9800" }} />,
    TRAINING: <PsychologyIcon fontSize="small" sx={{ color: "#00bcd4" }} />,
    DEBUG: <BugReportIcon fontSize="small" sx={{ color: "#9c27b0" }} />,
};

const typeColors = {
    INFO: "#2196f3",
    SUCCESS: "#4caf50",
    WARNING: "#ff9800",
    TRAINING: "#00bcd4",
    DEBUG: "#9c27b0",
};

const AgentLogPanel = ({ open, onClose, agent }) => {
    const [logs, setLogs] = useState([]);
    const [expanded, setExpanded] = useState({});
    const [feedback, setFeedback] = useState("");
    const [commPanelOpen, setCommPanelOpen] = useState(false);

    useEffect(() => {
        if (open && agent) {
            setLogs(generateDummyLogs(agent));
        }
    }, [open, agent]);

    const toggleExpand = (id) => {
        setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const handleFeedbackSubmit = () => {
        if (!feedback.trim()) return;
        const newEntry = {
            id: logs.length + 1,
            type: "TRAINING",
            title: `[HUMAN FEEDBACK] ${feedback}`,
            details: ["This feedback will be reviewed for retraining."],
            actor: "Human",
            time: new Date().toLocaleTimeString(),
        };
        setLogs([newEntry, ...logs]);
        setFeedback("");
    };

    if (!open) return null;

    return (
        <>
            <Modal open={open} onClose={onClose}>
                <Paper
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        p: 3,
                        width: "90vw",
                        maxWidth: 850,
                        maxHeight: "85vh",
                        overflowY: "auto",
                        bgcolor: "#111",
                        color: "#fff",
                        borderRadius: 2,
                        border: "1px solid #333",
                    }}
                >
                    {/* HEADER */}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 2,
                        }}
                    >
                        <Typography variant="h6" sx={{ color: "#12c099", fontWeight: 600 }}>
                            {agent} — Activity Timeline
                        </Typography>
                        <IconButton onClick={onClose} sx={{ color: "#fff" }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* TIMELINE */}
                    <Box sx={{ position: "relative", pl: 3, borderLeft: "2px solid #333" }}>
                        {logs.map((log) => (
                            <Box key={log.id} sx={{ mb: 3, position: "relative" }}>
                                {/* Dot */}
                                <Box
                                    sx={{
                                        position: "absolute",
                                        left: -11,
                                        top: 5,
                                        width: 14,
                                        height: 14,
                                        borderRadius: "50%",
                                        bgcolor: typeColors[log.type] || "#555",
                                        boxShadow: "0 0 10px rgba(18,192,153,0.5)",
                                    }}
                                />

                                {/* Entry Card */}
                                <Box
                                    sx={{
                                        p: 2,
                                        bgcolor: "#1a1a1a",
                                        borderRadius: 2,
                                        border: "1px solid #222",
                                        "&:hover": { borderColor: typeColors[log.type] },
                                        transition: "0.2s",
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        {typeIcons[log.type]}
                                        <Typography variant="subtitle2" sx={{ color: "#fff", flexGrow: 1 }}>
                                            {log.title}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: "#888" }}>
                                            {log.time}
                                        </Typography>
                                        <IconButton size="small" onClick={() => toggleExpand(log.id)} sx={{ color: "#12c099" }}>
                                            {expanded[log.id] ? <ArrowDropDownIcon /> : <ArrowRightIcon />}
                                        </IconButton>
                                    </Stack>

                                    <Collapse in={expanded[log.id]}>
                                        <Box sx={{ mt: 1.5, pl: 4 }}>
                                            {log.details.map((line, i) => (
                                                <Typography
                                                    key={i}
                                                    variant="body2"
                                                    sx={{ color: "#aaa", mb: 0.5, fontSize: "0.85rem" }}
                                                >
                                                    • {line}
                                                </Typography>
                                            ))}
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: "#666",
                                                    display: "block",
                                                    mt: 0.5,
                                                    fontStyle: "italic",
                                                }}
                                            >
                                                Actor: {log.actor}
                                            </Typography>
                                        </Box>
                                    </Collapse>
                                </Box>
                            </Box>
                        ))}
                    </Box>

                    <Divider sx={{ borderColor: "#333", my: 3 }} />

                    {/* FEEDBACK AREA */}
                    <Typography variant="subtitle2" sx={{ mb: 1, color: "#aaa" }}>
                        Human Feedback
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        placeholder={`Add feedback for ${agent}...`}
                        value={feedback}
                        onChange={(e) => setFeedback(e.target.value)}
                        variant="outlined"
                        sx={{
                            bgcolor: "#121212",
                            borderRadius: 1,
                            mb: 2,
                            "& .MuiOutlinedInput-root fieldset": { borderColor: "#333" },
                            "& .MuiInputBase-input": { color: "#fff" },
                        }}
                    />

                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Button
                            variant="outlined"
                            sx={{ borderColor: "#12c099", color: "#12c099", fontWeight: 600 }}
                            onClick={() => setCommPanelOpen(true)}
                        >
                            View Communications
                        </Button>
                        <Button
                            variant="contained"
                            sx={{ bgcolor: "#12c099", color: "#000", fontWeight: 600 }}
                            onClick={handleFeedbackSubmit}
                        >
                            Submit Feedback
                        </Button>
                    </Box>
                </Paper>
            </Modal>

            {/* Linked Communication Panel */}
            <AgentCommunicationPanel
                open={commPanelOpen}
                onClose={() => setCommPanelOpen(false)}
                agent={agent}
            />
        </>
    );
};

export default AgentLogPanel;
